import { Printer, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { buildOwnerLetterHtml } from "../lib/ownerLetterHtml";
import { printOwnerLetter } from "../lib/ownerLetterPrint";

/**
 * Shows the owner notice on screen before it goes anywhere near a printer.
 *
 * Until Nov 2026 a new impound fired `printOwnerLetter` straight away, so the
 * first time anyone saw the letter was in the browser's own print dialog —
 * with nothing to check the owner's name and address against beforehand.
 *
 * Renders the EXACT snapshot that was saved to `impound_letters`, through the
 * same `buildOwnerLetterHtml` the printer uses, so what is on screen is what
 * comes out of the tray.
 */
export default function OwnerLetterPreviewDialog({ snapshot, open, onOpenChange }) {
  if (!snapshot) return null;

  const print = () => {
    printOwnerLetter(snapshot);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-4xl max-h-[92vh] flex flex-col"
        data-testid="owner-letter-auto-preview"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Printer className="h-4.5 w-4.5" />
            Owner letter ready
          </DialogTitle>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Check the owner&apos;s name and address, then print. It&apos;s saved
            either way — you can re-print it from the impound at any time.
          </p>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-hidden rounded-md border border-slate-200 dark:border-slate-700 bg-white">
          <iframe
            title="Owner letter preview"
            srcDoc={buildOwnerLetterHtml(snapshot)}
            className="w-full h-full min-h-[55vh]"
            data-testid="owner-letter-auto-preview-frame"
          />
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="owner-letter-auto-preview-close"
          >
            <X className="h-4 w-4 mr-1.5" />
            Not now
          </Button>
          <Button onClick={print} data-testid="owner-letter-auto-preview-print">
            <Printer className="h-4 w-4 mr-1.5" />
            Print letter
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
